import { PhotoIcon, ArrowLeftIcon, UserIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import MainButton from "../component/sharedComponents/MainButton";
import PhoneInputField from "../component/sharedComponents/PhoneInputField";
import InputField from "../component/sharedComponents/InputField";
import TextareaField from "../component/sharedComponents/TextareaField";
import SelectField from "../component/sharedComponents/SelectField";
import DateField from "../component/sharedComponents/DateField";
import { handleFileUpload } from "../utils/uploadFiles";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { updateMyProfile } from "../slices/1-auth/2-myProfile/thunk";
import avatar from "../assets/images/Avatar.avif"
import { changePassword } from "../slices/1-auth/1-login/thunk";

const EditProfilePage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("profile");
    const dispatch = useDispatch();
    const { profile } = useSelector((state) => state.myProfile);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);


    const [passwordForm, setPasswordForm] = useState({
        oldPassword: "",
        newPassword: "",
        confirmNewPassword: "",
    });

    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        phone: "",
        avatar: "",
        coverImage: "",
        bio: "",
        gender: "",
        birthDate: "",
    });

    useEffect(() => {
        if (profile) {
            setForm({
                firstName: profile?.firstName || "",
                lastName: profile?.lastName || "",
                phone: profile?.phone || "",
                avatar: profile?.avatar || avatar,
                coverImage: profile?.coverImage || "",
                bio: profile?.bio || "",
                gender: profile?.gender || "",
                birthDate: profile?.birthDate ? formatDateForInput(profile?.birthDate) : "",
            });
        }
    }, [profile]);


    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        const payload = {
            ...form,
            fullName: `${form?.firstName} ${form?.lastName}`,
        };
        // console.log(payload)
        try {
            const resultAction = await dispatch(updateMyProfile(payload));

            if (updateMyProfile.fulfilled.match(resultAction)) {
                toast.success(t("profile.updatedSuccess"), { autoClose: 3000 });

                navigate("/profile");
            } else {
                // console.error("Failed to update profile", resultAction);
                // toast.error(resultAction.payload || "Failed to update profile");
            }
        } catch (error) {
            // console.error("Unexpected error", error);
            // toast.error("Something went wrong");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePasswordChange = (key, value) => {
        setPasswordForm(prev => ({ ...prev, [key]: value }));
    };
    const profileTabs = [
        { name: "profile", title: "profilePage.title", icon: UserIcon },
        { name: "password", title: "password.title", icon: LockClosedIcon },

    ];

    const formatDateForInput = (isoDate) => {
        if (!isoDate) return "";
        return isoDate.split("T")[0];
    };

    const isPasswordValid =
        passwordForm.oldPassword &&
        passwordForm.newPassword &&
        passwordForm.confirmNewPassword &&
        passwordForm.newPassword === passwordForm.confirmNewPassword;

    const handlePasswordSubmit = async () => {
        if (!isPasswordValid) return;
        setIsPasswordSubmitting(true);

        const payload = {
            oldPassword: passwordForm.oldPassword,
            newPassword: passwordForm.newPassword,
        };

        try {
            const resultAction = await dispatch(changePassword(payload));

            if (changePassword.fulfilled.match(resultAction)) {
                toast.success(t("profile.passwordChanged"));
                setPasswordForm({
                    oldPassword: "",
                    newPassword: "",
                    confirmNewPassword: "",
                });
            } else {
                // toast.error(resultAction.payload || "Failed to change password");
            }
        } catch (error) {
            // toast.error("Something went wrong");
        } finally {
            setIsPasswordSubmitting(false);
        }
    };

    const today = new Date();
    const maxBirthDate = new Date(
        today.getFullYear() - 18,
        today.getMonth(),
        today.getDate()
    )
        .toISOString()
        .split("T")[0]; // YYYY-MM-DD
    // console.log(form)
    return (
        <div className="max-w-3xl mx-auto">
            {/* TABS */}
            <div className="border-b border-border-light dark:border-border-dark">
                <div className="flex w-full gap-8 ">
                    {profileTabs.map(tab => {
                        const Icon = tab.icon;
                        const active = activeTab === tab.name;

                        return (
                            <button
                                key={tab.name}
                                onClick={() => setActiveTab(tab.name)}
                                className={`
          py-4 text-sm font-medium relative 
          flex items-center justify-center gap-2
          transition-colors
          ${active
                                        ? "dark:text-white text-black"
                                        : "text-text-muted-light dark:text-text-muted-dark hover:text-black dark:hover:text-white"}
        `}
                            >
                                <Icon className="w-5 h-5" />
                                <span>{t(`${tab.title}`)}</span>

                                {active && (
                                    <span className="absolute bottom-0 left-0 w-full h-0.5 dark:bg-primary bg-primary-light rounded-full" />
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* TAB CONTENT */}
            <div className="pt-6">
                {activeTab === "profile" && <section>

                    {/* FORM */}
                    <div className="p-4 space-y-4 bg-bg-light-secondary dark:bg-bg-dark-secondary rounded-2xl">

                        {/* PROFILE IMAGE */}
                        <div className="space-y-2">
                            <h3 className="text-xl font-medium text-black dark:text-white">
                                {t("profile.picture")}
                            </h3>

                            <div className="flex items-center gap-4">
                                {/* Avatar */}
                                <div className="relative">
                                    <img
                                        src={form?.avatar || avatar}
                                        alt="avatar"
                                        className="object-cover w-16 h-16 rounded-full lg:h-20 lg:w-20"
                                    />

                                    {/* Camera icon */}
                                    <label className="absolute bottom-0 right-0 p-2 rounded-full cursor-pointer bg-primary-light dark:bg-primary">
                                        <PhotoIcon className="w-4 h-4 text-white" />
                                        <input
                                            type="file"
                                            accept="image/png, image/jpeg"
                                            hidden
                                            onChange={(e) =>
                                                handleFileUpload(dispatch, e.target.files[0], "images", (url) =>
                                                    setForm((prev) => ({ ...prev, avatar: url }))
                                                )
                                            }
                                        />
                                    </label>
                                </div>

                                {/* Info */}
                                <div className="space-y-1">
                                    <label
                                        className="px-4 py-2 text-sm border cursor-pointer rounded-xl border-border-light dark:border-border-dark hover:bg-bg-light-tertiary dark:hover:bg-bg-dark-tertiary"
                                    >
                                        {t("profile.changeCover")}
                                        <input
                                            type="file"
                                            accept="image/png, image/jpeg"
                                            hidden
                                            onChange={(e) =>
                                                handleFileUpload(dispatch, e.target.files[0], "images", (url) =>
                                                    setForm((prev) => ({ ...prev, coverImage: url }))
                                                )
                                            }
                                        />
                                    </label>

                                    <p className="pt-2 text-xs text-text-muted-light dark:text-text-muted-dark">
                                        JPG, PNG. Max 5MB
                                    </p>
                                </div>

                            </div>
                        </div>

                        {/* Names */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="flex flex-col gap-1 text-start">
                                <label>{t("profile.firstName")}</label>
                                <InputField type="text" name="firstName"
                                    laceholder={t("profile.firstName")} value={form?.firstName} onChange={handleChange} />
                            </div>
                            <div className="flex flex-col gap-1 text-start">
                                <label>{t("profile.lastName")}</label>
                                <InputField type="text" name="lastName" laceholder={t("profile.lastName")} value={form?.lastName} onChange={handleChange} />
                            </div>
                        </div>

                        {/* Phone */}
                        <div className="flex flex-col gap-1 text-start">
                            <label>{t("profile.phone")}</label>
                            <PhoneInputField
                                value={form?.phone}
                                onChange={(value) =>
                                    setForm(prev => ({ ...prev, phone: value }))
                                }
                            />
                        </div>

                        {/* Gender + Birthdate */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="flex flex-col gap-1 text-start">
                                <label>{t("profile.gender")}</label>
                                <SelectField
                                    value={form?.gender}
                                    onChange={(e) =>
                                        setForm(prev => ({ ...prev, gender: e.target.value }))
                                    }
                                    placeholder={t("profile.gender")}
                                    options={[
                                        { value: "male", label: t("profile.male") },
                                        { value: "female", label: t("profile.female") },
                                    ]}
                                />
                            </div>
                            <div className="flex flex-col gap-1 text-start">
                                <label>{t("profile.birthDate")}</label>

                                <DateField
                                    value={form?.birthDate}
                                    max={maxBirthDate}
                                    onChange={(e) => {
                                        const selectedDate = new Date(e.target.value);
                                        const minAllowed = new Date(
                                            today.getFullYear() - 18,
                                            today.getMonth(),
                                            today.getDate()
                                        );

                                        if (selectedDate > minAllowed) {
                                            toast.error(t("profile.birthDateMin"));
                                            return;
                                        }

                                        setForm((prev) => ({ ...prev, birthDate: e.target.value }));
                                    }}
                                />
                            </div>

                        </div>

                        <div className="flex flex-col gap-1 text-start">
                            <label>{t("profile.bio")}</label>
                            <TextareaField
                                name="bio"
                                placeholder={t("profile.bio")}
                                value={form?.bio}
                                onChange={(e) => handleChange(e)}
                            />
                        </div>


                        {/* SAVE */}
                        <div className="flex justify-end">
                            <MainButton onClick={handleSubmit} disabled={isSubmitting}>
                                {isSubmitting ? t("profile.saving") : t("profile.save")}
                            </MainButton>
                        </div>
                    </div>
                </section>}
                {activeTab === "password" && (
                    <div className="space-y-4">
                        < div className="p-4 space-y-4 bg-bg-light-secondary dark:bg-bg-dark-secondary rounded-2xl">
                            <div className="space-y-2">
                                <label>{t("password.current")}</label>
                                <InputField
                                    type="password"
                                    placeholder={t("password.current")}
                                    value={passwordForm?.oldPassword}
                                    onChange={(e) =>
                                        handlePasswordChange("oldPassword", e.target.value)
                                    }
                                />
                            </div>
                            <div className="space-y-2">
                                <label>{t("password.new")}</label>
                                <InputField
                                    type="password"
                                    placeholder={t("password.new")}
                                    value={passwordForm?.newPassword}
                                    onChange={(e) =>
                                        handlePasswordChange("newPassword", e.target.value)
                                    }
                                />
                            </div>

                            <div className="space-y-2">
                                <label>{t("password.confirm")}</label>
                                <InputField
                                    type="password"
                                    placeholder={t("password.confirm")}
                                    value={passwordForm?.confirmNewPassword}
                                    onChange={(e) =>
                                        handlePasswordChange("confirmNewPassword", e.target.value)
                                    }
                                />
                            </div>

                            <div className="flex justify-start">
                                <MainButton
                                    disabled={!isPasswordValid || isPasswordSubmitting}
                                    onClick={handlePasswordSubmit}
                                >
                                    {isPasswordSubmitting ? t("password.saving") : t("password.save")}
                                </MainButton>

                            </div>
                        </div>
                    </div>
                )}

            </div>

        </div>
    );
};

export default EditProfilePage;
